import { useState, useEffect } from 'react';
import { Check, X, FileText, Truck, Factory, PackageCheck, ArrowRight, ChevronDown, MapPin, Clock, Sparkles, ShieldCheck } from 'lucide-react';
import LeadCaptureModal from '@/components/LeadCaptureModal';
import HeroCarousel, { CAROUSEL_IMAGES } from '@/components/HeroCarousel';

const STEPS = [
    { icon: <FileText />, title: 'Share drawing / cutlist', desc: 'Send your 2D/3D drawing or a ready cutlist.' },
    { icon: <PackageCheck />, title: 'We share board count', desc: 'Exact number of boards required for your build.' },
    { icon: <Truck />, title: 'You ship the material', desc: 'Send boards, laminates & edgebands to our NCR factory.' },
    { icon: <Factory />, title: 'We manufacture & dispatch', desc: 'Factory-finish carcass ready for installation.' },
];

const TIERS = [
    { key: 'prelam', label: 'Prelam Boards', price: 1200, blurb: 'Modular carcass built with pre-pasted (prelam) boards. Fastest to manufacture.', includes: ['Cutting to size', 'Edgebanding', 'Drilling & holing', 'Adhesive application'] },
    { key: 'postlam', label: 'Postlam Boards', price: 1500, blurb: 'Plain boards + your chosen liner laminate, pasted in-house.', includes: ['Cutting to size', 'Custom laminate pasting', 'Edgebanding', 'Drilling & holing'] },
];

const COMPARISON = [
    { icon: <Clock className="w-4 h-4" />, row: 'Timeline', carpenter: '2–3 months on-site', kanbr: '7 days factory-made*' },
    { icon: <Sparkles className="w-4 h-4" />, row: 'Daily mess & dust', carpenter: 'Yes, ongoing for weeks', kanbr: 'None — delivered finished' },
    { icon: <Factory className="w-4 h-4" />, row: 'Quality consistency', carpenter: 'Varies by worker & site', kanbr: 'Factory precision, jig-controlled' },
    { icon: <ShieldCheck className="w-4 h-4" />, row: 'Warranty', carpenter: 'Typically none', kanbr: 'Basis the material purchased' },
];

const SERVICE_AREAS = ['Delhi', 'Gurgaon', 'Noida', 'Faridabad', 'Ghaziabad', 'Haryana'];

const FAQS = [
    {
        q: 'How is Kanbr job work different from hiring a local carpenter?',
        a: 'A local carpenter builds your modular kitchen or wardrobe on-site — 2–3 months of daily mess, dust and unpredictable finish quality. Kanbr Job Rate is factory-made in our Delhi NCR unit in about 7 days. You source the boards and hardware; we cut, edgeband, drill and finish under jig-controlled factory precision, then deliver ready-to-install carcasses across Delhi, Gurgaon, Noida, Faridabad, Ghaziabad and the rest of Haryana. No carpenter delay, no dust, no on-site rework.',
    },
    {
        q: 'Why is my carpenter taking so long? What actually causes the delay?',
        a: 'On-site carpentry is inherently slow because each cut, edgeband and drill happens sequentially by one team, weather affects finish coats, tools get shared across jobs, and any measurement mistake means a fresh board is bought and the timeline resets. Our factory runs panel saws, edgebanders and CNC drills in parallel — a 10-board kitchen carcass that takes a carpenter 6–8 weeks is dispatched in roughly a week.',
    },
    {
        q: 'Factory-made modular kitchen vs carpenter — what does it cost?',
        a: 'Kanbr job work rate starts at ₹1,200 per board (Prelam) and ₹1,500 per board (Postlam) plus GST and transport. A comparable on-site carpenter kitchen in Delhi NCR typically bills ₹300–₹500 per sq ft of labor plus wastage, material spoilage and rework. On a standard 10 x 8 modular kitchen the two usually land within 10% of each other — but Kanbr gives you factory precision, a fixed delivery date, and none of the daily mess.',
    },
    {
        q: 'Which cities do you cover for job work in Delhi NCR?',
        a: 'We serve Delhi, Gurgaon (Gurugram), Noida, Faridabad, Ghaziabad and the wider Haryana region from our factory in Kundli, Sonipat. Modular kitchen job work Delhi, wardrobe labor charges Noida, carpenter job work rate Faridabad or Ghaziabad — same rate card, same 7-day turnaround, delivered by our transport partners.',
    },
    {
        q: 'What material do I need to send to you?',
        a: 'For Prelam Boards (₹1,200/board): pre-laminated MDF or plywood boards plus matching edgebands. For Postlam Boards (₹1,500/board): plain boards, your chosen liner laminate and edgebands. Hardware (hinges, drawer channels, handles) is procured by you separately and installed post-delivery. We share the exact board count and material spec after reviewing your drawing or cutlist.',
    },
];

const FAQ_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
    })),
};

const SERVICE_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Modular Kitchen Job Work & Wardrobe Manufacturing',
    provider: {
        '@type': 'LocalBusiness',
        name: 'Kanbr Concepts',
        url: 'https://www.kanbr.com',
        telephone: '+91-9773704406',
        address: {
            '@type': 'PostalAddress',
            addressLocality: 'Kundli',
            addressRegion: 'Delhi NCR',
            addressCountry: 'IN',
        },
    },
    areaServed: SERVICE_AREAS.map((c) => ({ '@type': 'City', name: c })),
    offers: [
        { '@type': 'Offer', name: 'Prelam Boards', price: '1200', priceCurrency: 'INR', description: 'Modular kitchen job work rate — prelam board manufacturing per board.' },
        { '@type': 'Offer', name: 'Postlam Boards', price: '1500', priceCurrency: 'INR', description: 'Wardrobe making labor charges — postlam board manufacturing per board.' },
    ],
};

function useJobWorkSEO() {
    useEffect(() => {
        const prevTitle = document.title;
        const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute('content');
        const prevCanonical = document.querySelector('link[rel="canonical"]')?.getAttribute('href');

        document.title = 'Modular Kitchen Job Work — Delhi, Gurgaon, Noida, Faridabad, Ghaziabad | Kanbr Concepts';

        const setMeta = (selector, attr, value) => {
            const el = document.querySelector(selector);
            if (el) el.setAttribute(attr, value);
        };
        setMeta('meta[name="description"]', 'content',
            'Factory-made modular kitchen and wardrobe job work in Delhi NCR — ready in 7 days instead of 2–3 months of carpenter mess. Modular kitchen job work rate ₹1,200/board (Prelam), ₹1,500/board (Postlam). Wardrobe labor charges across Delhi, Gurgaon, Noida, Faridabad, Ghaziabad, Haryana.'
        );
        setMeta('link[rel="canonical"]', 'href', 'https://www.kanbr.com/job-work');
        setMeta('meta[property="og:url"]', 'content', 'https://www.kanbr.com/job-work');
        setMeta('meta[property="og:title"]', 'content', 'Modular Kitchen Job Work in Delhi NCR — Factory-made in 7 Days');
        setMeta('meta[property="og:description"]', 'content', 'Skip the carpenter mess. Factory-finished modular kitchen and wardrobe carcass in about a week. Serving Delhi, Gurgaon, Noida, Faridabad, Ghaziabad and Haryana.');

        return () => {
            document.title = prevTitle;
            if (prevDesc) setMeta('meta[name="description"]', 'content', prevDesc);
            if (prevCanonical) setMeta('link[rel="canonical"]', 'href', prevCanonical);
        };
    }, []);
}

function FAQItem({ q, a, idx }) {
    const [open, setOpen] = useState(idx === 0);
    return (
        <div className="border border-slate-200 bg-white" data-testid={`jobwork-faq-item-${idx}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid={`jobwork-faq-toggle-${idx}`}
                className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50"
            >
                <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base leading-snug">{q}</h3>
                <ChevronDown className={`w-4 h-4 mt-1 text-slate-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="px-5 pb-5 text-sm text-slate-700 leading-relaxed" data-testid={`jobwork-faq-answer-${idx}`}>
                    {a}
                </div>
            )}
        </div>
    );
}

export default function JobWork() {
    const [leadOpen, setLeadOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const proceed = (tier) => { setSelected(tier); setLeadOpen(true); };
    useJobWorkSEO();

    return (
        <div>
            {/* JSON-LD structured data — picked up by Google + AI crawlers */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }}
            />

            <section className="bg-white border-b border-slate-200">
                <div className="kanbr-container py-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-7">
                        <div className="eyebrow text-slate-500 mb-2">Offering 01</div>
                        <h1 className="font-display text-2xl md:text-3xl font-bold text-amber-600 leading-tight tracking-tight">Job Work</h1>
                        <div className="font-display text-lg md:text-xl font-semibold text-slate-900 mt-2">You Source. We Manufacture. Ready in 7 days.</div>
                        <p className="text-slate-600 mt-3 text-sm md:text-base max-w-xl leading-relaxed">
                            Modular kitchen job work rate and wardrobe labor charges — factory-made in Delhi NCR, without the 2–3 months of carpenter mess. Ready-to-install carcass delivered across Delhi, Gurgaon, Noida, Faridabad, Ghaziabad and Haryana.
                        </p>
                        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid="jobwork-service-areas">
                            <MapPin className="w-3.5 h-3.5 text-blue-900" />
                            <span className="font-medium text-slate-800">Serving:</span>
                            {SERVICE_AREAS.map((c) => (
                                <span key={c} className="px-2 py-0.5 border border-slate-200 bg-white">{c}</span>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <HeroCarousel images={CAROUSEL_IMAGES} interval={3000} alt="Kanbr modular kitchen job work Delhi NCR" />
                    </div>
                </div>
            </section>

            <section className="kanbr-container py-10">
                <div className="eyebrow mb-2">How it works</div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-6">Four simple steps.</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {STEPS.map((st, i) => (
                        <div key={i} className="border border-slate-200 bg-white p-4 relative">
                            <div className="text-[10px] font-display font-bold text-amber-600 absolute top-3 right-4 text-lg">0{i + 1}</div>
                            <div className="w-10 h-10 bg-blue-900 text-white flex items-center justify-center mb-3">{st.icon}</div>
                            <h3 className="font-display font-semibold text-slate-900 text-sm">{st.title}</h3>
                            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{st.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="bg-white border-t border-slate-200">
                <div className="kanbr-container py-10">
                    <div className="eyebrow mb-2">Step 1 · Select board type</div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-2">Choose your Job Work tier.</h2>
                    <p className="text-slate-600 max-w-2xl text-sm leading-relaxed">Prices cover manufacturing only. Hardware, installation, GST and transport are additional.</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                        {TIERS.map((t) => (
                            <div key={t.key} className="border border-slate-200 bg-white p-6 flex flex-col" data-testid={`jobwork-tier-${t.key}`}>
                                <div className="eyebrow mb-2">{t.label}</div>
                                <div className="font-display text-4xl font-bold text-slate-900">₹{t.price}<span className="text-base font-medium text-slate-500"> /board</span></div>
                                <div className="text-xs text-slate-500 mt-1">+ GST + Transport</div>
                                <p className="text-slate-600 text-sm mt-3 leading-relaxed">{t.blurb}</p>
                                <ul className="mt-3 space-y-2 flex-1">
                                    {t.includes.map((line) => (
                                        <li key={line} className="flex items-start gap-2 text-sm text-slate-700">
                                            <Check className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> {line}
                                        </li>
                                    ))}
                                </ul>
                                <button onClick={() => proceed(t)} data-testid={`jobwork-select-${t.key}`} className="mt-5 btn-primary rounded-none justify-center">
                                    Select &amp; Continue <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 p-4 border-l-4 border-amber-600 bg-amber-50 text-xs text-slate-700">
                        <strong className="text-slate-900">Note:</strong> Prices are for manufacturing only. Hardware, installation, GST and transport are extra.
                    </div>
                </div>
            </section>

            {/* Local Carpenter vs Kanbr Job-Work comparison (positioned AFTER tier selection per user request) */}
            <section className="bg-slate-50 border-t border-slate-200" data-testid="jobwork-comparison-section">
                <div className="kanbr-container py-10">
                    <div className="eyebrow mb-2">The honest comparison</div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-2">Local Carpenter vs Kanbr Job-Work.</h2>
                    <p className="text-slate-600 max-w-2xl text-sm leading-relaxed">
                        If you're stuck deciding between hiring a carpenter for modular kitchen work or going factory-made — here's the side-by-side on what actually matters.
                    </p>

                    <div className="mt-6 overflow-hidden border border-slate-200 bg-white">
                        <table className="w-full text-sm" data-testid="jobwork-comparison-table">
                            <thead className="bg-slate-100 border-b border-slate-200">
                                <tr>
                                    <th className="text-left px-4 py-3 font-display font-semibold text-slate-900 w-1/3">Compare on</th>
                                    <th className="text-left px-4 py-3 font-display font-semibold text-slate-500">Local Carpenter</th>
                                    <th className="text-left px-4 py-3 font-display font-semibold text-amber-700 bg-amber-50">Kanbr Job-Work</th>
                                </tr>
                            </thead>
                            <tbody>
                                {COMPARISON.map((c, i) => (
                                    <tr key={c.row} className={i % 2 ? 'bg-white' : 'bg-slate-50/40'} data-testid={`jobwork-comparison-row-${i}`}>
                                        <td className="px-4 py-3 text-slate-800 font-medium flex items-center gap-2">
                                            <span className="text-blue-900">{c.icon}</span> {c.row}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">
                                            <span className="inline-flex items-center gap-2">
                                                <X className="w-3.5 h-3.5 text-red-500 flex-shrink-0" /> {c.carpenter}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-900 bg-amber-50/40">
                                            <span className="inline-flex items-center gap-2">
                                                <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0" /> {c.kanbr}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-3">*Standard 8–12 board modular kitchen or wardrobe. Complex custom builds may take longer — we confirm a firm date after cutlist review.</p>
                </div>
            </section>

            {/* NEW: FAQ section with FAQPage schema */}
            <section className="bg-slate-50 border-t border-slate-200" data-testid="jobwork-faq-section">
                <div className="kanbr-container py-10">
                    <div className="eyebrow mb-2">Common questions</div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-6">Job Work vs Carpenter — answered.</h2>
                    <div className="grid grid-cols-1 gap-3 max-w-3xl">
                        {FAQS.map((f, i) => (
                            <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />
                        ))}
                    </div>
                </div>
            </section>

            <LeadCaptureModal
                open={leadOpen}
                onOpenChange={setLeadOpen}
                source="job_work"
                defaultMessage={selected ? `Hi Kanbr, I'd like to do Job Work using ${selected.label} at ₹${selected.price}/board.` : ''}
                extra={{ board_type: selected?.key }}
            />
        </div>
    );
}
